import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Package, 
  Box, 
  ShoppingBag, 
  Tag,
  ChevronDown,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

gsap.registerPlugin(ScrollTrigger);

const Solutions = () => {
  const sectionsRef = useRef([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    // Scroll reveal animations for all sections
    sectionsRef.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(section,
          {
            y: 40,
            opacity: 0
          },
          {
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
              toggleActions: 'play none none none'
            },
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power3.out'
          }
        );
      }
    });
  }, []);

  const solutions = [
    {
      icon: Package,
      title: 'Custom Boxes',
      description: 'Rigid and folding boxes designed for presentation, protection, and brand impact.',
      image: 'https://images.unsplash.com/photo-1631010231931-d2c396b444ec?w=400',
      anchor: 'custom-boxes'
    },
    {
      icon: Box,
      title: 'Corrugated Packaging',
      description: 'Shipping-ready corrugated solutions built for strength and stacking performance.',
      image: 'https://images.unsplash.com/photo-1618381297523-e6c0ab13a5b2?w=400',
      anchor: 'corrugated'
    },
    {
      icon: ShoppingBag,
      title: 'E-commerce Mailer Boxes',
      description: 'Premium mailers for D2C and ecommerce, optimized for unboxing and logistics.',
      image: 'https://images.unsplash.com/photo-1766040923580-16ad32fae8b4?w=400',
      anchor: 'mailers'
    },
    {
      icon: ShoppingBag,
      title: 'Paper Bags',
      description: 'Retail and gifting bags with clean branding, strong handles, and quality finishes.',
      image: 'https://images.unsplash.com/photo-1765282946661-1975d58f9bf6?w=400',
      anchor: 'paper-bags'
    },
    {
      icon: Tag,
      title: 'Labels and Stickers',
      description: 'Product labels, branding stickers, and compliance labels with precise printing.',
      image: 'https://images.unsplash.com/photo-1668089968498-c05baaf1ad31?w=400',
      anchor: 'labels'
    },
    {
      icon: Tag,
      title: 'Tags and Inserts',
      description: 'Hang tags, care tags, thank you cards, and inserts that complete the brand story.',
      image: 'https://images.pexels.com/photos/9594430/pexels-photo-9594430.jpeg?w=400',
      anchor: 'tags'
    }
  ];

  const deepDiveSolutions = [
    {
      id: 'custom-boxes',
      title: 'Custom Boxes',
      intro: 'Designed for premium presentation and durability.',
      bestFor: 'Retail, gifting, premium product packaging',
      materials: 'Duplex board, kraft board, SBS, art paper wrapping',
      finishes: 'Matte, gloss, lamination, foil, emboss, spot UV',
      useCases: 'Cosmetics, confectionery, electronics, gifting',
      image1: 'https://images.unsplash.com/photo-1631010231888-777b6285ef84?w=600',
      image2: 'https://images.pexels.com/photos/7957723/pexels-photo-7957723.jpeg?w=600'
    },
    {
      id: 'corrugated',
      title: 'Corrugated Packaging',
      intro: 'Industrial-strength shipping solutions for secure transportation.',
      bestFor: 'Shipping, storage, bulk packaging, industrial products',
      materials: 'Single-wall, double-wall, triple-wall corrugated flutes (B, C, E)',
      finishes: 'Kraft, white-top, printed, laminated',
      useCases: 'E-commerce, logistics, manufacturing, export packaging',
      image1: 'https://images.unsplash.com/photo-1769355104335-acef3aa4c9b6?w=600',
      image2: 'https://images.pexels.com/photos/7309940/pexels-photo-7309940.jpeg?w=600'
    },
    {
      id: 'mailers',
      title: 'E-commerce Mailer Boxes',
      intro: 'Unboxing-ready packaging for direct-to-consumer brands.',
      bestFor: 'D2C brands, online retail, subscription boxes',
      materials: 'Corrugated kraft, duplex board, microflute',
      finishes: 'Custom printing, inside printing, tear strips, tamper-proof seals',
      useCases: 'Apparel, beauty products, electronics, subscription services',
      image1: 'https://images.pexels.com/photos/12053213/pexels-photo-12053213.jpeg?w=600',
      image2: 'https://images.unsplash.com/photo-1573376671096-e1fce2d1f19d?w=600'
    },
    {
      id: 'paper-bags',
      title: 'Paper Bags',
      intro: 'Sustainable retail and gifting bags with premium finishes.',
      bestFor: 'Retail stores, boutiques, events, gifting',
      materials: 'Kraft paper, art paper, coated paper',
      finishes: 'Twisted handles, flat handles, ribbon handles, matte/gloss lamination',
      useCases: 'Fashion retail, gifting, food packaging, event merchandise',
      image1: 'https://images.unsplash.com/photo-1633414654227-2b5ea828b3ef?w=600',
      image2: 'https://images.pexels.com/photos/5556289/pexels-photo-5556289.jpeg?w=600'
    },
    {
      id: 'labels',
      title: 'Labels and Stickers',
      intro: 'High-precision labels for product branding and compliance.',
      bestFor: 'Product labeling, branding, regulatory compliance',
      materials: 'Paper, vinyl, PET, holographic, transparent',
      finishes: 'Gloss, matte, metallic, waterproof, tamper-evident',
      useCases: 'Food & beverage, cosmetics, pharmaceuticals, logistics',
      image1: 'https://images.unsplash.com/photo-1669028689447-d4d92994cee8?w=600',
      image2: 'https://images.pexels.com/photos/32424228/pexels-photo-32424228.jpeg?w=600'
    },
    {
      id: 'tags',
      title: 'Tags and Inserts',
      intro: 'Finishing touches that complete your packaging story.',
      bestFor: 'Garments, accessories, premium products',
      materials: 'Cardstock, kraft paper, art paper, string/ribbon',
      finishes: 'Die-cut shapes, foil stamping, embossing, perforation',
      useCases: 'Apparel tags, care instructions, thank you cards, brand inserts',
      image1: 'https://images.unsplash.com/photo-1669894197890-f3ddaaf9331f?w=600',
      image2: 'https://images.unsplash.com/photo-1567570671138-76c7e06caa3b?w=600'
    }
  ];

  const materials = [
    { name: 'Kraft', description: 'Natural, eco-friendly, cost-effective' },
    { name: 'Duplex Board', description: 'Sturdy, smooth finish, printable' },
    { name: 'Corrugated', description: 'Multiple flute types for protection' },
    { name: 'Matte Lamination', description: 'Soft touch, premium feel' },
    { name: 'Gloss Lamination', description: 'Vibrant colors, protection' },
    { name: 'Foil / UV', description: 'Metallic effects, spot highlights' }
  ];

  const industries = [
    {
      name: 'E-commerce and D2C',
      description: 'Mailer boxes, shipping packaging, unboxing experiences'
    },
    {
      name: 'Retail and Lifestyle',
      description: 'Custom boxes, shopping bags, brand packaging'
    },
    {
      name: 'Food and Beverage',
      description: 'Food-safe boxes, labels, takeaway packaging'
    },
    {
      name: 'Cosmetics and Personal Care',
      description: 'Luxury boxes, labels, rigid packaging'
    },
    {
      name: 'Pharma and FMCG',
      description: 'Compliance labels, protective packaging'
    },
    {
      name: 'Logistics and Manufacturing',
      description: 'Corrugated boxes, industrial packaging, bulk orders'
    }
  ];

  const faqs = [
    {
      question: 'Do you support small and large order quantities?',
      answer: 'Yes, we cater to both small batch orders and large-scale production runs. Minimum order quantities vary by product type and customization level.'
    },
    {
      question: 'Can you help with design and prototyping?',
      answer: 'Absolutely. Our in-house design team assists with structural design, artwork, and material selection. We provide physical samples before full production.'
    },
    {
      question: 'What is the typical production timeline?',
      answer: 'Standard orders take 10-15 business days after design approval. Rush orders and larger volumes may have different timelines depending on complexity.'
    },
    {
      question: 'What finishing options do you provide?',
      answer: 'We offer matte and gloss lamination, foil stamping, embossing, debossing, spot UV, die-cutting, and window patching across most product categories.'
    },
    {
      question: 'Do you deliver across India?',
      answer: 'Yes, we ship pan-India through our logistics partners. We also handle export orders for international clients.'
    }
  ];

  const scrollToSection = (anchor) => {
    const element = document.getElementById(anchor);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        ref={el => sectionsRef.current[0] = el}
        className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 via-white to-gray-50"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <p className="text-sm font-semibold text-navy-900 tracking-wider uppercase">
                Our Solutions
              </p>
              <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
                Comprehensive Packaging Solutions
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                From concept and prototyping to full-scale manufacturing, we deliver packaging 
                that elevates your brand and performs reliably in real-world conditions.
              </p>
            </div>

            {/* Right Image */}
            <div className="relative h-96 lg:h-[500px]">
              <div className="absolute inset-0 grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img 
                    src="https://images.unsplash.com/photo-1631010231931-d2c396b444ec?w=600"
                    alt="Custom Packaging"
                    className="w-full h-56 object-cover rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                  />
                  <img 
                    src="https://images.unsplash.com/photo-1765282946661-1975d58f9bf6?w=600"
                    alt="Paper Bags"
                    className="w-full h-32 object-cover rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
                <div className="pt-8">
                  <img 
                    src="https://images.unsplash.com/photo-1618381297523-e6c0ab13a5b2?w=600"
                    alt="Corrugated Boxes"
                    className="w-full h-80 object-cover rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section 
        ref={el => sectionsRef.current[1] = el}
        className="py-20 bg-gray-50"
      >
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              What We Offer
            </h2>
            <p className="text-lg text-gray-600">
              End-to-end packaging solutions for every industry and application
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <Card 
                  key={index} 
                  className="group border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-navy-900/40 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg">
                      <IconComponent className="h-6 w-6 text-navy-900" />
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-3">
                    <h3 className="text-xl font-semibold text-navy-900">
                      {solution.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {solution.description}
                    </p>
                    <button
                      onClick={() => scrollToSection(solution.anchor)}
                      className="text-navy-900 font-medium hover:underline flex items-center gap-2 group/link"
                    >
                      Learn more 
                      <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deep Dive Sections */}
      {deepDiveSolutions.map((solution, index) => (
        <section 
          key={solution.id}
          id={solution.id}
          ref={el => sectionsRef.current[index + 2] = el}
          className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="container-custom">
            <div className={`grid lg:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <h2 className="text-4xl font-bold text-navy-900">
                  {solution.title}
                </h2>
                <p className="text-xl text-gray-700">
                  {solution.intro}
                </p>
                
                <div className="space-y-4 pt-4">
                  <div>
                    <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-2">
                      Best For
                    </h3>
                    <p className="text-gray-600">{solution.bestFor}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-2">
                      Materials
                    </h3>
                    <p className="text-gray-600">{solution.materials}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-2">
                      Finishes
                    </h3>
                    <p className="text-gray-600">{solution.finishes}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-2">
                      Typical Use Cases
                    </h3>
                    <p className="text-gray-600">{solution.useCases}</p>
                  </div>
                </div>

                <div className="pt-4">
                  <Button 
                    className="bg-navy-900 hover:bg-navy-800 text-white"
                    asChild
                  >
                    <Link to="/contact">
                      Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Images */}
              <div className={`space-y-4 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img 
                  src={solution.image1}
                  alt={`${solution.title} example 1`}
                  className="w-full h-72 object-cover rounded-lg shadow-lg"
                />
                <img 
                  src={solution.image2}
                  alt={`${solution.title} example 2`}
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Materials and Finishes */}
      <section 
        ref={el => sectionsRef.current[8] = el}
        className="py-20 bg-white"
      >
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Materials and Finishes
            </h2>
            <p className="text-lg text-gray-600">
              Material selection and finishing define how packaging looks, feels, and performs. 
              We help you choose based on durability, cost, and brand positioning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {materials.map((material, index) => (
              <div 
                key={index}
                className="p-6 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-navy-900 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-navy-900 mb-2">
                  {material.name}
                </h3>
                <p className="text-gray-600">{material.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section 
        ref={el => sectionsRef.current[9] = el}
        className="py-20 bg-gray-50"
      >
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600">
              Packaging expertise across diverse business sectors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="p-6 bg-white rounded-lg border-2 hover:border-navy-900 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-navy-900 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-navy-900 mb-2">
                      {industry.name}
                    </h3>
                    <p className="text-gray-600 text-sm">{industry.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        ref={el => sectionsRef.current[10] = el}
        className="py-20 bg-navy-900 text-white"
      >
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need packaging that fits your product and brand?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Share your requirement and we'll guide you to the right structure, material, and finish.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-navy-900 hover:bg-gray-100 text-lg px-8 py-6"
              asChild
            >
              <Link to="/portfolio">
                Explore Portfolio <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-navy-900 text-lg px-8 py-6"
              asChild
            >
              <Link to="/contact">
                Contact Us <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        ref={el => sectionsRef.current[11] = el}
        className="py-20 bg-white"
      >
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border-2 border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-navy-900 text-left">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`h-5 w-5 text-navy-900 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t-2">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
